// ABOUTME: Analytics card showing weekly behavior logging progress and trends
// ABOUTME: Displays current week total vs goal, trend vs previous week, and 4-week history

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { analyticsAPI } from '../services/api';
import { Child } from '../types/api';
import naluV1Theme from '../theme/theme.nalu-v1';

interface WeeklyRecordingCardProps {
  familyId: number;
  children: Child[];
  familyTimezone?: string;
}

interface WeeklyRecordingData {
  currentWeekTotal: number;
  weeklyGoal: number;
  previousWeekTotal: number | null;
  percentageComplete: number;
  trend: {
    direction: 'up' | 'down' | 'same' | 'first';
    displayValue: string;
  };
  weekHistory: {
    weekStartDate: string;
    total: number;
  }[];
}

const WeeklyRecordingCard: React.FC<WeeklyRecordingCardProps> = ({
  familyId,
  children,
  familyTimezone,
}) => {
  const theme = useTheme();
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const [data, setData] = useState<WeeklyRecordingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadWeeklyRecordingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadWeeklyRecordingData intentionally omitted; it is stable and adding it would cause extra fetches
  }, [familyId, children, familyTimezone]);

  const loadWeeklyRecordingData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await analyticsAPI.getWeeklyRecording(familyId, familyTimezone);
      setData(response);
    } catch (err: unknown) {
      console.error('WeeklyRecordingCard: Failed to load weekly recording data:', err);
      if (axios.isAxiosError(err)) {
        console.error('WeeklyRecordingCard: Error details:', err.response?.data, err.response?.status);
      }
      setError('Unable to load weekly recording data');
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (direction: string) => {
    switch (direction) {
      case 'up':
        return <TrendingUpIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />;
      case 'down':
        return <TrendingDownIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />;
      case 'same':
        return <RemoveIcon sx={{ color: theme.palette.grey[600], fontSize: 20 }} />;
      default:
        return null;
    }
  };

  const getTrendColor = (direction: string) => {
    switch (direction) {
      case 'up':
        return theme.palette.success.main;
      case 'down':
        return theme.palette.error.main;
      case 'same':
        return theme.palette.grey[600];
      case 'first':
        return theme.palette.info.main;
      default:
        return theme.palette.text.secondary;
    }
  };


  if (loading) {
    return (
      <Card sx={{ minHeight: 200 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 160 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ minHeight: 200 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            This Week&apos;s Recording
          </Typography>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  // Cap progress bar at 100% visually
  const displayProgress = Math.min(data.percentageComplete, 100);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, textAlign: 'center' }}>
          This Week&apos;s Recording
        </Typography>

        {/* Large Central Number */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography 
            variant="h1" 
            sx={{
              fontSize: '4rem',
              color: theme.palette.text.primary,
              lineHeight: 1
            }}
          >
            {data.currentWeekTotal}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: theme.palette.text.secondary,
              mt: 0.5
            }}
          >
            Behaviors Recorded
          </Typography>
        </Box>

        {/* Trend from Last Week */}
        {data.trend && data.trend.displayValue && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 1,
            mb: 3
          }}>
            {getTrendIcon(data.trend.direction)}
            <Typography 
              variant="body1" 
              sx={{
                color: getTrendColor(data.trend.direction),
                fontWeight: 600
              }}
            >
              {data.trend.displayValue}
            </Typography>
          </Box>
        )}
        

        {/* Weekly Goal Progress */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 1 
          }}>
            <Typography variant="body1">
              Weekly Goal: {data.weeklyGoal} Recordings
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600 }}
            >
              {Math.round(data.percentageComplete)}%
            </Typography>
          </Box>
          
          <LinearProgress 
            variant="determinate" 
            value={displayProgress}
            sx={{
              height: 8,
              borderRadius: isNaluV1Theme ? '48px' : 4,
              backgroundColor: theme.palette.grey[200],
              '& .MuiLinearProgress-bar': {
                borderRadius: isNaluV1Theme ? '48px' : 4,
                backgroundColor: theme.palette.warning.main
              }
            }}
          />
        </Box>

        {/* 4-Week Trend with Indicators */}
        {data.weekHistory.length > 0 && (
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center', 
            mt: 2
          }}>
            {/* Title with chart icon */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 1,
              mb: 1.5
            }}>
              <Box sx={{ 
                display: 'flex', 
                gap: 0.25, 
                alignItems: 'end', 
                height: 16 
              }}>
                <Box sx={{ 
                  width: 3, 
                  height: 6, 
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: isNaluV1Theme ? '3px' : 0.25
                }} />
                <Box sx={{ 
                  width: 3, 
                  height: 10, 
                  backgroundColor: theme.palette.secondary.main,
                  borderRadius: isNaluV1Theme ? '3px' : 0.25
                }} />
                <Box sx={{ 
                  width: 3, 
                  height: 8, 
                  backgroundColor: theme.palette.warning.main,
                  borderRadius: isNaluV1Theme ? '3px' : 0.25
                }} />
                <Box sx={{ 
                  width: 3, 
                  height: 12, 
                  backgroundColor: theme.palette.success.main,
                  borderRadius: isNaluV1Theme ? '3px' : 0.25
                }} />
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                4-Week Trend
              </Typography>
            </Box>
            {/* Week chips with arrows between them */}
            <Box
              data-testid="week-chips-container"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: 0.5, sm: 1 } // Reduced gap on mobile
              }}
            >
                {data.weekHistory.map((week, index) => {
                  let previousTotal: number;
                  if (index === 0) {
                    previousTotal = data.previousWeekTotal || 0;
                  } else {
                    previousTotal = data.weekHistory[index - 1].total;
                  }
                  
                  const change = week.total - previousTotal;
                  let percentageChange = 0;
                  if (previousTotal > 0) {
                    percentageChange = Math.round((change / previousTotal) * 100);
                  }
                  
                  let trendColor = theme.palette.text.secondary;
                  let trendIcon = null;
                  if (change > 0) {
                    trendIcon = <TrendingUpIcon sx={{ fontSize: 14 }} />;
                    trendColor = theme.palette.success.main;
                  } else if (change < 0) {
                    trendIcon = <TrendingDownIcon sx={{ fontSize: 14 }} />;
                    trendColor = theme.palette.error.main;
                  } else {
                    trendIcon = <RemoveIcon sx={{ fontSize: 14 }} />;
                    trendColor = theme.palette.grey[600];
                  }
                  
                  return (
                    <React.Fragment key={week.weekStartDate}>
                      {/* Week Chip */}
                      <Box
                        data-testid="week-chip"
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          backgroundColor: theme.palette.grey[50],
                          borderRadius: isNaluV1Theme ? '24px' : 2,
                          px: { xs: 1, sm: 1.5 }, // Reduced padding on mobile
                          py: { xs: 0.5, sm: 1 }, // Reduced padding on mobile
                          minWidth: { xs: 45, sm: 60 }, // Reduced width on mobile
                          border: `1px solid ${theme.palette.grey[300]}`
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            fontSize: { xs: '0.875rem', sm: '1rem' } // Explicit mobile font sizing
                          }}
                        >
                          {week.total}
                        </Typography>
                        <Box sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.25,
                          mt: 0.25
                        }}>
                          {trendIcon}
                          <Typography
                            variant="caption"
                            sx={{
                              color: trendColor,
                              fontSize: { xs: '0.6rem', sm: '0.65rem' }, // Even smaller on mobile
                              fontWeight: 600
                            }}
                          >
                            {change > 0 ? '+' : ''}{percentageChange}%
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Arrow between chips (except after last chip) */}
                      {index < data.weekHistory.length - 1 && (
                        <Typography sx={{
                          color: theme.palette.text.secondary,
                          mx: { xs: 0.25, sm: 0.5 }, // Reduced margin on mobile
                          fontSize: { xs: '0.8rem', sm: '1rem' } // Smaller arrows on mobile
                        }}>
                          →
                        </Typography>
                      )}
                    </React.Fragment>
                  );
              })}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default WeeklyRecordingCard;